import {
  attributeDefinitionIdFormat,
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-ids/ids";
import * as z from "zod";
import {
  CalculatedEbayListingDataSchema,
  EbayMappingSchema,
  EbayProductMapping,
  EbayVariantMapping,
  EbayVariantMappingSchema,
} from "../marketplace/ebay-listing";
import { ListingType } from "./base-listing";
import {
  ChatGptListingSchema,
  ChatGptMappingSchema,
} from "./chatgpt-listing/chatgpt-listing";
import {
  ChatGptVariantListingSchema,
  ChatGptVariantMappingSchema,
} from "./chatgpt-listing/chatgpt-variant";
import { ProductFamilyDetailsSchema } from "./general-listing/general-listing";
import { ProductDetailsSchema } from "./general-listing/general-listing-variant";
import {
  GoogleShoppingListingSchema,
  GoogleShoppingMappingSchema,
} from "./google-shopping-listing/google-shopping-listing";
import {
  GoogleShoppingVariantListingSchema,
  GoogleShoppingVariantMappingSchema,
} from "./google-shopping-listing/google-shopping-variant";
import {
  RedoMarketplaceMappingSchema,
  RedoMarketplaceVariantMappingSchema,
} from "./redo-marketplace-listing/redo-marketplace-listing";
import {
  ShopifyListingSchema,
  ShopifyMappingSchema,
} from "./shopify-listing/shopify-listing";
import {
  ShopifyVariantListingSchema,
  ShopifyVariantMappingSchema,
} from "./shopify-listing/shopify-variant";

export const ListingTypeToFriendlyName = {
  [ListingType.General]: "General",
  [ListingType.Shopify]: "Shopify",
  [ListingType.ChatGpt]: "ChatGPT",
  [ListingType.Amazon]: "Amazon",
  [ListingType.Walmart]: "Walmart",
  [ListingType.Perplexity]: "Perplexity",
  [ListingType.RedoMarketplace]: "Redo",
  [ListingType.Ebay]: "eBay",
  [ListingType.GoogleShopping]: "Google/Gemini",
} satisfies Record<ListingType, string>;

const StandardMappingSchema_ = z.object({
  fieldName: z.string(),
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const StandardMappingSchema: z.ZodType<StandardMapping> =
  StandardMappingSchema_;
export interface StandardMapping
  extends z.infer<typeof StandardMappingSchema_> {}

const DefaultListingMappingSchema = z.object({
  productMappings: z.array(StandardMappingSchema).readonly(),
  variantMappings: z.array(StandardMappingSchema).readonly(),
});

export const ListingMappingSchema = {
  [ListingType.General]: DefaultListingMappingSchema,
  [ListingType.Shopify]: ShopifyMappingSchema,
  [ListingType.ChatGpt]: ChatGptMappingSchema,
  [ListingType.Amazon]: DefaultListingMappingSchema,
  [ListingType.Walmart]: DefaultListingMappingSchema,
  [ListingType.Perplexity]: DefaultListingMappingSchema,
  [ListingType.RedoMarketplace]: RedoMarketplaceMappingSchema,
  [ListingType.Ebay]: EbayMappingSchema,
  [ListingType.GoogleShopping]: GoogleShoppingMappingSchema,
};

export type ListingMappingType = {
  [key in ListingType]: (typeof ListingMappingSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export type ListingMappings = { [key in ListingType]: ListingMappingType[key] };

export function getEbayMappingFieldName(
  mapping: EbayProductMapping | EbayVariantMapping,
): string {
  return mapping.fieldName === "aspect"
    ? `aspect:${mapping.aspectName}`
    : mapping.fieldName;
}

export const ProductMappingSchema = {
  [ListingType.General]: z.array(StandardMappingSchema).readonly(),
  [ListingType.Shopify]: z.array(ShopifyVariantMappingSchema).readonly(),
  [ListingType.ChatGpt]: z.array(ChatGptVariantMappingSchema).readonly(),
  [ListingType.Amazon]: z.array(StandardMappingSchema).readonly(),
  [ListingType.Walmart]: z.array(StandardMappingSchema).readonly(),
  [ListingType.Perplexity]: z.array(StandardMappingSchema).readonly(),
  [ListingType.RedoMarketplace]: z
    .array(RedoMarketplaceVariantMappingSchema)
    .readonly(),
  [ListingType.Ebay]: z.array(EbayVariantMappingSchema).readonly(),
  [ListingType.GoogleShopping]: z
    .array(GoogleShoppingVariantMappingSchema)
    .readonly(),
};

export type ProductMappingType = {
  [key in ListingType]: (typeof ProductMappingSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export type ProductMappings = { [key in ListingType]: ProductMappingType[key] };

export const ProductFamilyListingSchema = {
  [ListingType.General]: ProductFamilyDetailsSchema,
  [ListingType.Shopify]: ShopifyListingSchema,
  [ListingType.ChatGpt]: ChatGptListingSchema,
  [ListingType.RedoMarketplace]: z.object({
    listingType: z.literal(ListingType.RedoMarketplace),
    productFamilyId: productFamilyIdFormat.schema,
  }),
  [ListingType.Ebay]: CalculatedEbayListingDataSchema,
  [ListingType.Amazon]: z.object({
    listingType: z.literal(ListingType.Amazon),
    productFamilyId: productFamilyIdFormat.schema,
  }),
  [ListingType.Walmart]: z.object({
    listingType: z.literal(ListingType.Walmart),
    productFamilyId: productFamilyIdFormat.schema,
  }),
  [ListingType.Perplexity]: z.object({
    listingType: z.literal(ListingType.Perplexity),
    productFamilyId: productFamilyIdFormat.schema,
  }),
  [ListingType.GoogleShopping]: GoogleShoppingListingSchema,
};

export type ProductFamilyListingType = {
  [key in ListingType]: (typeof ProductFamilyListingSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export const ProductListingSchema = {
  [ListingType.General]: ProductDetailsSchema,
  [ListingType.Shopify]: ShopifyVariantListingSchema,
  [ListingType.ChatGpt]: ChatGptVariantListingSchema,
  [ListingType.RedoMarketplace]: z.object({
    listingType: z.literal(ListingType.RedoMarketplace),
    productId: productIdFormat.schema,
  }),
  [ListingType.Ebay]: z.object({
    listingType: z.literal(ListingType.Ebay),
    productId: productIdFormat.schema,
  }),
  [ListingType.Amazon]: z.object({
    listingType: z.literal(ListingType.Amazon),
    productId: productIdFormat.schema,
  }),
  [ListingType.Walmart]: z.object({
    listingType: z.literal(ListingType.Walmart),
    productId: productIdFormat.schema,
  }),
  [ListingType.Perplexity]: z.object({
    listingType: z.literal(ListingType.Perplexity),
    productId: productIdFormat.schema,
  }),
  [ListingType.GoogleShopping]: GoogleShoppingVariantListingSchema,
};
export type ProductListingType = {
  [key in ListingType]: (typeof ProductListingSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};
